import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Save, Upload, Download, Trash2 } from "lucide-react";
import {
  getToken, fetchSettings, saveProfile, saveFiscal, saveLogo, changeEmail, changePassword,
  clearToken, requestAccountDeletion,
} from "@/lib/mastroAuth";
import { useInstallCapability } from "@/hooks/useInstallCapability";
import { SETTINGS } from "@/constants/testIds";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Card = ({ title, children }) => (
  <section className="mt-6 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
    <div className="bg-neutral-950 px-5 py-4">
      <h2 className="text-base font-bold text-white md:text-lg">{title}</h2>
    </div>
    <div className="px-5 py-5">{children}</div>
  </section>
);

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-medium uppercase text-neutral-400">{label}</span>
    <input {...props} className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20" />
  </label>
);

const primaryBtn = "inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700";
const secondaryBtn = "inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50";

const InstallCard = () => {
  const { standalone, ios, canInstall, installed, promptInstall } = useInstallCapability();

  return (
    <Card title="Installa Mastro">
      <p data-testid={SETTINGS.installCard} className="text-sm text-neutral-600">
        Aggiungi Mastro alla schermata Home per aprirlo come un'app, senza cambiare il modo in cui lo usi.
      </p>
      <div className="mt-4">
        {installed || standalone ? (
          <p data-testid={SETTINGS.installStatusInstalled} className="text-sm font-medium text-neutral-700">
            Mastro è installato su questo dispositivo.
          </p>
        ) : canInstall ? (
          <button data-testid={SETTINGS.installButton} onClick={() => promptInstall()} className={primaryBtn}>
            <Download className="h-4 w-4" /> Installa Mastro
          </button>
        ) : ios ? (
          <p data-testid={SETTINGS.installIosHint} className="text-sm text-neutral-600">
            Su iPhone: apri Condividi e scegli "Aggiungi alla schermata Home".
          </p>
        ) : (
          <p data-testid={SETTINGS.installStatusNeutral} className="text-sm text-neutral-500">
            L'installazione non è disponibile su questo browser.
          </p>
        )}
      </div>
    </Card>
  );
};

export default function Impostazioni() {
  const token = getToken();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [profile, setProfile] = useState({});
  const [fiscal, setFiscal] = useState({});
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState({ current: "", next: "" });
  // MS-L6B — Pre-Beta account deletion request (double confirmation).
  const [deleteConfirm1Open, setDeleteConfirm1Open] = useState(false);
  const [deleteConfirmFinalOpen, setDeleteConfirmFinalOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(null), 3000); };

  const hydrate = (d) => {
    setData(d);
    setProfile(d.profile || {});
    setFiscal(d.fiscal || {});
    setEmail(d.account?.recovery_email || "");
  };

  useEffect(() => {
    fetchSettings(token).then(hydrate).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-3 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>;

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return flash("File non valido: usa un'immagine.");
    if (f.size > 500 * 1024) return flash("Logo troppo grande (max 500KB).");
    const r = new FileReader();
    r.onload = async () => {
      await saveLogo(token, r.result);
      const d = await fetchSettings(token);
      hydrate(d);
      flash("Logo aggiornato.");
    };
    r.readAsDataURL(f);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid={SETTINGS.page}>
      <h1 className="text-2xl font-bold tracking-tight">Impostazioni</h1>
      {msg && <div data-testid={SETTINGS.toast} className="mt-3 rounded-lg bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 ring-1 ring-inset ring-teal-600/20">{msg}</div>}

      <Card title="Intestazione preventivo">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Denominazione" data-testid={SETTINGS.profileName} value={profile.business_name || ""} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} />
          <Field label="Ragione sociale" data-testid={SETTINGS.profileLegal} value={profile.legal_name || ""} onChange={(e) => setProfile({ ...profile, legal_name: e.target.value })} />
          <Field label="P.IVA" data-testid={SETTINGS.profileVat} value={profile.vat_number || ""} onChange={(e) => setProfile({ ...profile, vat_number: e.target.value })} />
          <Field label="Indirizzo / CAP" data-testid={SETTINGS.profileAddress} value={profile.address_cap || ""} onChange={(e) => setProfile({ ...profile, address_cap: e.target.value })} />
          <Field label="E-mail" data-testid={SETTINGS.profileContact} value={profile.contact_info || ""} onChange={(e) => setProfile({ ...profile, contact_info: e.target.value })} />
          <Field label="Telefono / Cellulare" data-testid={SETTINGS.profilePhone} value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button data-testid={SETTINGS.profileSave} onClick={async () => { const d = await saveProfile(token, profile); hydrate(d); flash("Profilo salvato."); }}
            className={primaryBtn}><Save className="h-4 w-4" /> Salva profilo</button>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <Upload className="h-4 w-4" /> {data?.profile?.has_logo ? "Cambia logo" : "Carica logo"}
            <input data-testid={SETTINGS.logoInput} type="file" accept="image/*" className="hidden" onChange={onLogo} />
          </label>
          {data?.profile?.logo_url && (
            <img
              data-testid="settings-logo-preview"
              src={data.profile.logo_url}
              alt="Logo azienda"
              className="h-12 w-12 rounded-lg border border-neutral-200 object-contain bg-white"
            />
          )}
        </div>
      </Card>

      <Card title="Configurazione preventivi / IVA">
        <p className="mb-3 text-xs text-neutral-500">Dichiara esplicitamente il tuo regime. Vale come default dei nuovi preventivi (l'override del singolo preventivo prevale). Non riscrive i preventivi già emessi.</p>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input data-testid={SETTINGS.fiscalApplied} type="checkbox" checked={!!fiscal.vat_applied} onChange={(e) => setFiscal({ ...fiscal, vat_applied: e.target.checked })} className="accent-teal-600" /> IVA applicata
          </label>
          <label className="flex items-center gap-2 text-sm">Aliquota %
            <input data-testid={SETTINGS.fiscalRate} type="number" value={fiscal.default_vat_rate ?? "0"} disabled={!fiscal.vat_applied}
              onChange={(e) => setFiscal({ ...fiscal, default_vat_rate: e.target.value })}
              className="w-20 rounded border border-neutral-300 px-2 py-1 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 disabled:bg-neutral-100" />
          </label>
          <label className="flex items-center gap-2 text-sm">Prezzi
            <select data-testid={SETTINGS.fiscalMode} value={fiscal.pricing_mode || "excluded"} disabled={!fiscal.vat_applied}
              onChange={(e) => setFiscal({ ...fiscal, pricing_mode: e.target.value })}
              className="rounded border border-neutral-300 px-2 py-1 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 disabled:bg-neutral-100">
              <option value="excluded">IVA esclusa</option>
              <option value="included">IVA inclusa</option>
            </select>
          </label>
        </div>
        <button data-testid={SETTINGS.fiscalSave} onClick={async () => {
          try { const d = await saveFiscal(token, { vat_applied: !!fiscal.vat_applied, default_vat_rate: String(fiscal.default_vat_rate ?? "0"), pricing_mode: fiscal.pricing_mode || "excluded" }); hydrate(d); flash("Configurazione IVA salvata."); }
          catch { flash("Valori IVA non validi."); }
        }} className={`mt-4 ${primaryBtn}`}><Save className="h-4 w-4" /> Salva IVA</button>
      </Card>

      <Card title="Account">
        <div className="rounded-lg bg-neutral-100 px-3 py-2 text-sm">
          <span className="text-neutral-500">Numero WhatsApp verificato</span>
          <div data-testid={SETTINGS.accountWhatsapp} className="font-medium text-neutral-950">{data?.account?.whatsapp_number || "—"} <span className="ml-2 text-xs text-neutral-400">(sola lettura)</span></div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <Field label="Email di recupero" data-testid={SETTINGS.accountEmail} value={email} onChange={(e) => setEmail(e.target.value)} />
            <button data-testid={SETTINGS.accountEmailSave} onClick={async () => {
              try { await changeEmail(token, email); flash("Email aggiornata."); } catch (e) { flash(e.response?.status === 409 ? "Email già in uso." : "Email non valida."); }
            }} className={`mt-2 ${secondaryBtn}`}>Aggiorna email</button>
          </div>
          <div>
            <Field label="Password attuale" data-testid={SETTINGS.pwCurrent} type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
            <div className="mt-2"><Field label="Nuova password" data-testid={SETTINGS.pwNew} type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></div>
            <button data-testid={SETTINGS.pwSave} onClick={async () => {
              try { await changePassword(token, pw.current, pw.next); setPw({ current: "", next: "" }); flash("Password aggiornata."); }
              catch (e) { flash(e.response?.status === 401 ? "Password attuale errata." : "Nuova password non valida (min 8)."); }
            }} className={`mt-2 ${secondaryBtn}`}>Cambia password</button>
          </div>
        </div>

        {/* MS-L6B — Eliminazione account (Pre-Beta lightweight) */}
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50/50 p-4">
          <h3 className="text-sm font-semibold text-red-800">Eliminazione account</h3>
          <p className="mt-1 text-sm text-red-700/90">
            Richiedi l’eliminazione del tuo account Mastro. Dopo la conferma non potrai più accedere.
          </p>
          <button
            type="button"
            data-testid={SETTINGS.accountDeleteButton}
            onClick={() => setDeleteConfirm1Open(true)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" /> Richiedi eliminazione account
          </button>
        </div>
      </Card>

      {/* MS-L6B — First confirmation dialog */}
      <AlertDialog open={deleteConfirm1Open} onOpenChange={setDeleteConfirm1Open}>
        <AlertDialogContent data-testid={SETTINGS.accountDeleteConfirm1Dialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Vuoi richiedere l’eliminazione del tuo account Mastro?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={SETTINGS.accountDeleteConfirm1Cancel}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid={SETTINGS.accountDeleteConfirm1}
              onClick={(e) => {
                e.preventDefault();
                setDeleteConfirm1Open(false);
                setDeleteConfirmFinalOpen(true);
              }}
            >
              Prosegui
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* MS-L6B — Second (final, destructive) confirmation dialog */}
      <AlertDialog open={deleteConfirmFinalOpen} onOpenChange={setDeleteConfirmFinalOpen}>
        <AlertDialogContent data-testid={SETTINGS.accountDeleteConfirmFinalDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione account</AlertDialogTitle>
            <AlertDialogDescription>
              Dopo la conferma non potrai più accedere al tuo account. La cancellazione definitiva dei dati verrà completata tramite procedura amministrativa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={SETTINGS.accountDeleteConfirmFinalCancel} disabled={deleteSubmitting}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={SETTINGS.accountDeleteConfirmFinal}
              disabled={deleteSubmitting}
              onClick={async (e) => {
                e.preventDefault();
                if (deleteSubmitting) return;
                setDeleteSubmitting(true);
                try {
                  await requestAccountDeletion(token);
                  clearToken();
                  navigate("/login", {
                    replace: true,
                    state: { deletionRequested: true },
                  });
                } catch (_) {
                  // Even on error, drop the local token and route back to login
                  // so the disabled account cannot linger in a half-authenticated
                  // state. The backend is the source of truth.
                  clearToken();
                  navigate("/login", {
                    replace: true,
                    state: { deletionRequested: true },
                  });
                }
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Conferma eliminazione account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InstallCard />
    </motion.div>
  );
}
